import React, { useEffect, useRef, useState } from "react"

import H from "components/atoms/H"
import P from "components/atoms/P"
import Button from "components/atoms/Button"
import trackingCategoriesHelper from "utils/trackingCategoriesHelper"
import { useIntl } from "gatsby-plugin-intl"
import PropTypes from "prop-types"
import paths from "utils/paths"
import lottie from "lottie-web"
import HomeHeroAnimation from "assets/animations/home-hero-animation.json"
import {
  StyledHeroSection,
  SectionContentWrapper,
  paragraphStyles,
  StyledHeroAnimation,
  StyledButtonWrapper,
} from "./HomepageHeroSection.styles"

const HomepageHeroSection = ({ sectionData }) => {
  const { header, description } = sectionData
  const { about } = paths
  const { formatMessage } = useIntl()
  const [anim, setAnim] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const animationContainer = useRef()

  useEffect(() => {
    const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)")
    const hero = lottie.loadAnimation({
      container: animationContainer.current,
      renderer: "svg",
      loop: true,
      // Skip the flag-only tail after the buildings end.
      initialSegment: [0, 450],
      autoplay: !motionPreference.matches,
      animationData: HomeHeroAnimation,
    })
    if (motionPreference.matches) hero.goToAndStop(449, true)
    setIsPlaying(!motionPreference.matches)
    const onMotionChange = event => {
      if (event.matches) {
        hero.pause()
        setIsPlaying(false)
      }
    }
    motionPreference.addEventListener("change", onMotionChange)
    setAnim(hero)
    return () => {
      motionPreference.removeEventListener("change", onMotionChange)
      hero.destroy()
    }
  }, [])

  return (
    <StyledHeroSection>
      <SectionContentWrapper>
        <H as="h1">{header}</H>
        <P extraStyles={paragraphStyles}>{description}</P>
        <StyledButtonWrapper>
          <Button
            to={about}
            type="secondaryFilled"
            trackingOptions={{
              category: `${trackingCategoriesHelper.HOME} button`,
              action: "Click",
              label: `${trackingCategoriesHelper.HOME} hero button(Learn more))`,
            }}
          >
            {formatMessage({
              id: `pages.home.hero.button`,
            })}
          </Button>
        </StyledButtonWrapper>
      </SectionContentWrapper>
      <div style={{ position: "relative", minWidth: 0, paddingBottom: 56 }}>
        <StyledHeroAnimation ref={animationContainer} aria-hidden="true" />
          <button
            type="button"
            aria-pressed={isPlaying}
            onClick={() => {
              if (!anim) return
              if (isPlaying) {
                anim.pause()
                setIsPlaying(false)
              } else {
                if (anim.currentFrame >= anim.totalFrames - 1) {
                  anim.goToAndPlay(0, true)
                } else {
                  anim.play()
                }
                setIsPlaying(true)
              }
            }}
            style={{
              position: "absolute",
              bottom: 0,
              right: 0,
              padding: "10px 16px",
              minHeight: 44,
              background: "white",
              color: "#021f4b",
              border: "1px solid #021f4b",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            {isPlaying ? "Pause" : "Play"}
          </button>
      </div>
    </StyledHeroSection>
  )
}

export default HomepageHeroSection

HomepageHeroSection.propTypes = {
  sectionData: PropTypes.shape({
    header: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
}
